import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Quote } from "lucide-react";
import { useEffect } from "react";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet";
import ExploreMore from "@/components/ExploreMore";
import LazyImage from "@/components/LazyImage";
import useCanonical from "@/hooks/useCanonical";
import useMetaDescription from "@/hooks/useMetaDescription";

// Certificate <picture> helper — kept at module scope so the <source> is an
// unconditional direct child of <picture>. See LazyImage.jsx for the same reason.
function CertPicture({ src, alt }) {
  const isRaster = typeof src === "string" && /\.(png|jpe?g)$/i.test(src);
  const webp = isRaster ? src.replace(/\.(png|jpe?g)$/i, ".webp") : null;
  // Inline <img> in both branches — see LazyImage.jsx for why.
  if (webp) {
    return (
      <picture>
        <source srcSet={webp} type="image/webp" />
        <img src={src} alt={alt} className="w-full h-auto object-contain" loading="lazy" decoding="async" />
      </picture>
    );
  }
  return <img src={src} alt={alt} className="w-full h-auto object-contain" loading="lazy" decoding="async" />;
}

const feedbackCertificates = [
  { id: 1, company: "Avi Designs", image: "/testimonials/client-feedback-01.jpg", caption: "Letter of Appreciation — Avi Designs" },
  { id: 2, company: "Time Technoplast Ltd.", image: "/testimonials/client-feedback-02.png", caption: "Client Feedback — Time Technoplast Ltd." },
  { id: 3, company: "Encompass", image: "/testimonials/client-feedback-03.png", caption: "Client Feedback — Encompass" },
  { id: 4, company: "ARP Infra", image: "/testimonials/client-feedback-04.jpg", caption: "Client Feedback — ARP Infra" },
];

const writtenTestimonials = [
  {
    id: 1,
    company: "Avi Designs",
    logo: "/testimonials/avi-design.jpg",
    text: "Ezeefit has been our trusted partner for multiple corporate projects. Their quality, timely delivery, and professional approach make them stand out.",
    author: "Design Director",
  },
  {
    id: 2,
    company: "Time Technoplast Ltd.",
    logo: "/testimonials/Time-logo.jpg",
    text: "We've worked with Ezeefit on our corporate office expansion. The furniture quality exceeded our expectations and the team was highly professional.",
    author: "Facilities Manager",
  },
  {
    id: 3,
    company: "Encompass",
    logo: "/testimonials/Encompass-logo.jpg",
    text: "Excellent execution from design to installation. Ezeefit understood our requirements perfectly and delivered within the promised timeline.",
    author: "Project Head",
  },
  {
    id: 4,
    company: "ARP Infra",
    logo: "/testimonials/arp-infra-logo.jpg",
    text: "Reliable partner for all our furniture needs. Their customization capability and after-sales support are commendable.",
    author: "Operations Manager",
  },
];

export default function TestimonialsPage() {
  useCanonical("https://ezeefitofficesystems.in/testimonials");
  useMetaDescription("Client testimonials for Ezeefit modular office furniture — feedback from IT companies, banks, startups & public sector across Mumbai, Navi Mumbai, Thane.");
  useEffect(() => {
    document.title = "Client Testimonials | Corporate Office Furniture Mumbai | Ezeefit Office Systems";
  }, []);

  return (
    <div className="pt-20" data-testid="testimonials-page">
      <Helmet>
        <meta name="description" content="Client testimonials for Ezeefit modular office furniture — feedback from IT companies, banks, startups & public sector across Mumbai, Navi Mumbai, Thane." />
        <link rel="canonical" href="https://ezeefitofficesystems.in/testimonials" />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({"@context": "https://schema.org", "@type": "BreadcrumbList", "itemListElement": [{"@type": "ListItem", "position": 1, "name": "Home", "item": "https://ezeefitofficesystems.in"}, {"@type": "ListItem", "position": 2, "name": "Testimonials", "item": "https://ezeefitofficesystems.in/testimonials"}]}) }} />
      {/* Hero */}
      <section className="py-24 bg-[#0F172A] noise-overlay">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="font-mono text-sm text-[#F97316] tracking-wider uppercase mb-4 block">
              Client Stories
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              What Our Clients Say
            </h1>
            <p className="text-xl text-[#94A3B8] max-w-2xl mx-auto">
              Hear from corporates who have transformed their workspaces with EZEEFIT.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Written Testimonials */}
      <section className="py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="font-mono text-sm text-[#F97316] tracking-wider uppercase mb-4 block">
              Appreciation
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A]">
              Letters of Appreciation
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {writtenTestimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-8 bg-white border border-[#E2E8F0] card-hover"
              >
                <Quote className="w-10 h-10 text-[#F97316] mb-6" />
                <p className="text-[#334155] text-lg leading-relaxed mb-6">
                  "{testimonial.text}"
                </p>
                <div className="flex items-center gap-4">
                  <LazyImage
                    src={testimonial.logo}
                    alt={`${testimonial.company} logo - Ezeefit office furniture corporate client`}
                    containerClassName="w-16 h-16 bg-white border border-[#E2E8F0] flex items-center justify-center p-2"
                    className="max-w-full max-h-full object-contain"
                  />
                  <div>
                    <div className="font-bold text-[#0F172A]">{testimonial.company}</div>
                    <div className="text-sm text-[#64748B]">{testimonial.author}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Client Feedback Certificates */}
      <section className="py-20 bg-[#F8FAFC] border-t border-[#E2E8F0]" data-testid="feedback-certificates-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <span className="font-mono text-sm text-[#F97316] tracking-wider uppercase mb-3 block">
              Verified Client Feedback
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A]">
              Client Feedback Certificates
            </h2>
            <p className="text-base text-[#64748B] max-w-2xl mx-auto mt-4">
              Original appreciation letters and signed feedback received from corporate clients post project handover.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {feedbackCertificates.map((cert, i) => (
              <Dialog key={cert.id}>
                <DialogTrigger asChild>
                  <motion.button
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.08 }}
                    whileHover={{ y: -4 }}
                    className="group bg-white border border-[#E2E8F0] hover:border-[#F97316]/40 transition-all duration-300 overflow-hidden text-left"
                    data-testid={`feedback-certificate-${cert.id}`}
                  >
                    <div className="aspect-[3/4] bg-[#F1F5F9] overflow-hidden">
                      <LazyImage
                        src={cert.image}
                        alt={cert.caption}
                        containerClassName="w-full h-full"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="px-4 py-3 border-t border-[#E2E8F0]">
                      <p className="text-sm font-semibold text-[#0F172A] group-hover:text-[#F97316] transition-colors">
                        {cert.company}
                      </p>
                      <p className="text-xs text-[#64748B] mt-1">Click to view full certificate</p>
                    </div>
                  </motion.button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl bg-white p-2">
                  <CertPicture src={cert.image} alt={cert.caption} />
                </DialogContent>
              </Dialog>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#0F172A] noise-overlay">
        <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-24 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Join Our Growing List of Satisfied Clients
          </h2>
          <p className="text-lg text-[#94A3B8] mb-8">
            Let's create a success story together. See our{" "}
            <Link to="/case-studies" className="text-[#F97316] underline hover:text-orange-400">case studies</Link>{" "}
            or explore{" "}
            <Link to="/solutions" className="text-[#F97316] underline hover:text-orange-400">industry solutions</Link>.
          </p>
          <Button
            asChild
            className="bg-[#F97316] hover:bg-orange-600 text-white rounded-none px-10 py-6 font-semibold uppercase tracking-wider"
          >
            <Link to="/contact">
              Start Your Project
            </Link>
          </Button>
        </div>
      </section>

      <ExploreMore currentPage="testimonials" />
    </div>
  );
}
